import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { HttpErrorResponse, HttpEvent, HttpEventType } from '@angular/common/http';
import { SnackbarActionEnum, SnackBarService } from '../../services/utils/snack-bar.service';
import { UPLOADER_SERVICE_UNIQUE_IDS_ENUM } from '../../constants/shared/uploader-service-unique-ids';
import { catchErrorTypedAsync } from '../../services/functions-helper.service';

@Injectable({
    providedIn: 'root',
})
export class UploaderService {
    private _uploadSubjects = new Map<UPLOADER_SERVICE_UNIQUE_IDS_ENUM, Subject<number>>();

    private _newUploaderSubject = new Subject<{
        type: UPLOADER_SERVICE_UNIQUE_IDS_ENUM;
        observable: Observable<number>;
    }>();
    public newUploader$ = this._newUploaderSubject.asObservable();
    constructor(private snackBarService: SnackBarService) {}

    getUploadPercentageObservable(name: UPLOADER_SERVICE_UNIQUE_IDS_ENUM): Observable<number> | undefined {
        return this._uploadSubjects.get(name)?.asObservable();
    }

    uploadFileAsync<T>(
        type: UPLOADER_SERVICE_UNIQUE_IDS_ENUM,
        httpRequest: Observable<HttpEvent<T>>
    ): Promise<{ data: T; error: Error | HttpErrorResponse }> {
        // Create a new Subject if doesn't exist
        if (!this._uploadSubjects.has(type)) {
            const subject = new Subject<number>();
            this._uploadSubjects.set(type, subject);
            this._newUploaderSubject.next({ observable: subject.asObservable(), type: type });
        }

        const subject = this._uploadSubjects.get(type)!;
        subject.next(0);

        const localFileObject = {
            progress: 0,
        };

        return catchErrorTypedAsync(
            new Promise<T>((resolve, reject) => {
                httpRequest.subscribe({
                    next: (event) => {
                        if (event.type === HttpEventType.UploadProgress) {
                            const loaded = event['loaded'] || 0;
                            const total = event['total'] || 1; // Avoid division by zero
                            const progress = (loaded / total) * 100;

                            if (Math.round(localFileObject.progress) !== Math.round(progress)) {
                                localFileObject.progress = progress;
                                subject.next(localFileObject.progress);
                            }
                            return;
                        }
                        if (event.type === HttpEventType.Response) {
                            console.log('Uploaded Successfully');
                            subject.next(100);
                            subject.complete(); // Complete the subject after success
                            // this._uploadSubjects.delete(uniqueName); // Clean up
                            return resolve(event.body);
                        }
                    },
                    error: (error: any) => {
                        console.error(error);
                        let messageToDisplay = "We couldn't upload your file";

                        if (error instanceof HttpErrorResponse) {
                            if (error.status === 415) {
                                messageToDisplay = 'Unsupported file type';
                            }
                        }

                        this.snackBarService.openMessage(messageToDisplay, SnackbarActionEnum.Close, 5000);
                        subject.error(error);
                        this._uploadSubjects.delete(type); // Clean up on error
                        return reject(error);
                    },
                    complete: () => {
                        console.log('Upload observable completed');
                    },
                });
            })
        );
    }
}
